import React, { useState, useEffect } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalBody,
   ModalCloseButton,
   Button,
   Input,
   Checkbox,
   Avatar,
   Text,
} from '@chakra-ui/react';
import { AlertCircle, User, Mail, Phone, AlertTriangle } from 'lucide-react';

export default function DeleteCompanyModal({ isOpen, onClose, companyData, onConfirmDelete, isDark = false }) {
   const [deleteText, setDeleteText] = useState('');
   const [isChecked, setIsChecked] = useState(false);

   const isDeleteEnabled = deleteText === 'DELETE' && isChecked;

   // Reset state when modal closes
   useEffect(() => {
      if (!isOpen) {
         setDeleteText('');
         setIsChecked(false);
      }
   }, [isOpen]);

   const handleDelete = () => {
      if (isDeleteEnabled && onConfirmDelete) {
         onConfirmDelete();
      }
   };

   const handleClose = () => {
      setDeleteText('');
      setIsChecked(false);
      onClose();
   };

   return (
      <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered>
         <ModalOverlay className="bg-black bg-opacity-50" />
         <ModalContent className="mx-4">
            {/* Header with Error State */}
            <div className="bg-red-500 text-white px-6 py-4 rounded-t-md flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6" />
                  <div>
                     <h2 className="text-xl font-semibold">Delete Company</h2>
                     <p className="text-sm text-red-100">This action cannot be undone.</p>
                  </div>
               </div>
               <ModalCloseButton className="text-white hover:bg-red-600 rounded-md static mt-0" />
            </div>

            <ModalBody className="p-6">
               {/* Warning Section */}
               <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex items-start gap-3">
                     <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                     <div>
                        <h3 className="font-semibold text-yellow-800 mb-2">Warning: Permanent Deletion</h3>
                        <p className="text-sm text-yellow-700 mb-3">
                           You are about to permanently delete this company and all associated data. This includes:
                        </p>
                        <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                           <li className="list-disc">Company profile and GST/PAN information</li>
                           <li className="list-disc">All invoices, bills, and transaction records</li>
                           <li className="list-disc">Customer and vendor master data</li>
                           <li className="list-disc">Product and inventory records</li>
                           <li className="list-disc">Payment receipts and financial documents</li>
                           <li className="list-disc">Reports and activity logs</li>
                           <li className="list-disc">Bank accounts and reconciliation data</li>
                        </ul>
                     </div>
                  </div>
               </div>

               {/* Company Information */}
               {companyData && (
                  <div className="mb-6">
                     <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Company Information
                     </h3>
                     <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
                        <div className="flex items-center gap-3">
                           <div className="relative rounded-full w-10 h-10 overflow-hidden flex-shrink-0">
                              {companyData?.logo || companyData?.liveUrl ? (
                                 <img
                                    src={companyData.liveUrl || companyData.logo}
                                    crossOrigin="anonymous"
                                    alt="Company Logo"
                                    className="w-full h-full object-cover rounded-full"
                                 />
                              ) : (
                                 <Avatar
                                    name={companyData?.name || 'Company'}
                                    src="https://bit.ly/broken-link"
                                    size="sm"
                                    className="w-full h-full"
                                 />
                              )}
                           </div>
                           <div>
                              <Text fontSize="md" fontWeight="600" color="gray.800">
                                 {companyData?.name || 'N/A'}
                              </Text>
                           </div>
                        </div>

                        {companyData?.gstRegNo && (
                           <div className="flex items-center gap-3 text-sm text-gray-700">
                              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span>GST: {companyData.gstRegNo}</span>
                           </div>
                        )}

                        {companyData?.panNo && (
                           <div className="flex items-center gap-3 text-sm text-gray-700">
                              <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span>PAN: {companyData.panNo}</span>
                           </div>
                        )}

                        {companyData?.dbName && (
                           <div className="flex items-center gap-3 text-sm text-gray-700">
                              <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span>Database: {companyData.dbName}</span>
                           </div>
                        )}
                     </div>
                  </div>
               )}

               {/* Confirmation Input */}
               <div className="mb-6">
                  <label className="block text-sm text-gray-700 mb-2">
                     Type <span className="font-bold text-red-600">DELETE</span> to confirm
                  </label>
                  <Input
                     value={deleteText}
                     onChange={(e) => setDeleteText(e.target.value)}
                     placeholder="Type DELETE here"
                     className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                     size="md"
                  />
               </div>

               {/* Checkbox Confirmation */}
               <div className="mb-6">
                  <Checkbox isChecked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} className="text-sm">
                     <span className="text-sm text-gray-700">
                        I understand that this action is permanent and irreversible. All company data including
                        invoices, transactions, customers, vendors, and financial records will be permanently deleted.
                     </span>
                  </Checkbox>
               </div>

               {/* Action Buttons */}
               <div className="flex justify-end gap-3">
                  <Button onClick={handleClose} variant="outline" className="px-6 border-gray-300 hover:bg-gray-50">
                     Cancel
                  </Button>
                  <Button
                     onClick={handleDelete}
                     isDisabled={!isDeleteEnabled}
                     className={`px-6 ${
                        isDeleteEnabled
                           ? 'bg-red-500 hover:bg-red-600 text-white'
                           : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                     }`}>
                     <span className="mr-2">🗑️</span>
                     Permanently Delete
                  </Button>
               </div>
            </ModalBody>
         </ModalContent>
      </Modal>
   );
}
